import { PASTRY_COLORS } from "../../utilities/constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Typography,
  IconButton,
  styled,
  Skeleton,
  Container,
} from "@mui/material";
import { getColorForIndex } from "../../utilities";
import { useNavigate } from "react-router";
import { CategorySkeleton } from "../../pages/home/Skeleton";
import Grid from "@mui/material/Grid2";
import { CommonSection } from "../common/CommonSection";
import { useAuth } from "../../hooks/AuthContext";
import { useEffect, useRef, useState } from "react";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";

const NavigationButtons = styled(Box)({
  display: "flex",
  gap: "12px",
});

const NavButton = styled(IconButton)({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  border: "1px solid var(--neutral-200)",
  backgroundColor: "var(--neutral-white)",
  "&:hover": {
    backgroundColor: "var(--neutral-100)",
  },
  "&.next": {
    backgroundColor: "var(--neutral-black)",
    color: "var(--neutral-white)",
    border: "none",
    "&:hover": {
      backgroundColor: "var(--neutral-800)",
    },
  },
  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },
});

const ScrollContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    overflowX: "auto",
    overflowY: "hidden",
    "&::-webkit-scrollbar": {
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "var(--neutral-100)",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "var(--neutral-400)",
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: "var(--neutral-500)",
      },
    },
  },
}));

const MobileGrid = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    display: "grid",
    gridTemplateRows: "repeat(2, 1fr)",
    gridAutoFlow: "column",
    gap: "10px",
    width: "max-content",
    paddingBottom: "8px",
  },
}));

// ✅ CakeItem — desktop unchanged, mobile gets fixed width
const CakeItem = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  cursor: "pointer",
  [theme.breakpoints.down("sm")]: {
    minWidth: "80px",
    width: "95px",         // ✅ fixed width on mobile only
  },
}));

const CakeText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "1.4",
  color: "#1F2522",
  textAlign: "center",
  letterSpacing: "-0.01em",
  whiteSpace: "wrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    whiteSpace: "nowrap",
    width: "100%",
    textAlign: "center",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  lineHeight: "1.2",
  color: "#1F2522",
  textAlign: "left",
  marginBottom: "18px",
  fontSize: "28px",
  letterSpacing: "-0.04em",
  [theme.breakpoints.down("md")]: {
    fontSize: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
    marginBottom: "12px",
  },
}));

const Section = styled("section")(({ theme }) => ({
  padding: "20px",
  margin: "0 auto",
  maxWidth: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "1rem",
  marginTop: "1rem",
  [theme.breakpoints.down("md")]: {
    marginTop: "0.5rem",
  },
}));

// ✅ FlavourCard3D adapted for Category items
const CategoryFlavourCard = ({ item, onClick, index }) => {
  const [hover, setHover] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = screenWidth < 400;
  const isSmallTablet = screenWidth >= 400 && screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const isMobileView = screenWidth < 600;

  const getCharacterTransform = () => {
    if (!hover) return "none";
    if (isMobile) return "translate3d(0%, -30%, 100px)";
    else if (isSmallTablet) return "translate3d(0%, -20%, 100px)";
    else if (isTablet) return "translate3d(0%, -15%, 100px)";
    else return "translate3d(0%, -15%, 100px)";
  };

  const activeHover = isMobileView ? false : hover;

  return (
    <CakeItem
      onClick={onClick}
      onMouseEnter={() => !isMobileView && setHover(true)}
      onMouseLeave={() => !isMobileView && setHover(false)}
      sx={{
        width: "100%",
        background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(252,248,241,0.96) 100%)",
        border: "1px solid rgba(206, 177, 119, 0.18)",
        borderRadius: "18px",
        padding: "14px 12px 12px",
        transition: "all 0.25s ease",
        boxShadow: "0 10px 22px rgba(20, 18, 16, 0.04)",
        "&:hover": {
          borderColor: "rgba(183, 123, 26, 0.30)",
          boxShadow: "0 18px 36px rgba(183, 123, 26, 0.09)",
          transform: "translateY(-3px)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          aspectRatio: "1/1",
          borderRadius: "14px",
          background: "radial-gradient(circle at top, rgba(255,255,255,0.96) 0%, rgba(244,239,230,0.9) 50%, rgba(236,228,209,0.8) 100%)",
          overflow: "hidden",
          ...(isMobileView && {
            width: "75px",
            height: "75px",
            flexShrink: 0,
          }),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: activeHover ? 1 : 0.92,
            background: "radial-gradient(circle at 50% 18%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 55%)",
            transition: "all 0.35s ease",
          }}
        />
        <img
          src={item?.app_category_image}
          alt={item?.category_name}
          style={{
            width: "78%",
            height: "78%",
            objectFit: "contain",
            borderRadius: 12,
            transition: "transform 0.35s ease",
            transform: activeHover ? "scale(1.04)" : "scale(1)",
            zIndex: 1,
          }}
        />
      </Box>

      <CakeText variant="body2" sx={{ mt: 1.5 }}>{item?.category_name}</CakeText>
    </CakeItem>
  );
};

function Category({ data, isLoading }) {
  let navigate = useNavigate();
  const { setCategoryList } = useAuth();
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setShowLeftArrow(container.scrollLeft > 0);
    setShowRightArrow(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 5,
    );
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    handleScroll();
    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [data]);

  if (data?.products?.length == 0) return null;

  return (
    <Section>
      <Container maxWidth="none" sx={{py:"18px"}} className="common-container">
        <SectionHeader>
          {isLoading ? (
            <Skeleton variant="text" width="20%" height={40} />
          ) : (
            <SectionTitle variant="h4">{data?.headings}</SectionTitle>
          )}
        </SectionHeader>

        <div className="cake-grid">
          {isLoading ? (
            <CategorySkeleton />
          ) : (
            <>
              {/* ✅ Desktop and Tablet View — 100% ORIGINAL, nothing changed */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Grid
                  container
                  spacing={{ xs: 1.5, sm: 2.5, md: 6 }}
                  sx={{ justifyContent: "center" }}
                >
                  {data?.products?.map((item, index) => (
                    <Grid
                      key={index}
                      size={{ xs: 4, sm: 3, md: 2.4, lg: 2, xl: 2.4}}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <CategoryFlavourCard
                        item={item}
                        index={index}
                        onClick={() =>
                          navigate(`/listing/${item?.category_slug}`)
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Mobile View - 2 Rows Horizontal Scroll */}
              <Box
                sx={{
                  display: { xs: "block", sm: "none" },
                  position: "relative",
                }}
              >
                <ScrollContainer ref={scrollContainerRef}>
                  <MobileGrid>
                    {data?.products?.map((item, index) => (
                      <CategoryFlavourCard
                        key={index}
                        item={item}
                        index={index}
                        onClick={() =>
                          navigate(`/listing/${item?.category_slug}`)
                        }
                      />
                    ))}
                  </MobileGrid>
                </ScrollContainer>
              </Box>
            </>
          )}
        </div>
      </Container>
    </Section>
  );
}

export default Category;